import * as Sentry from '@sentry/react';
import type { LoginResult } from 'api/models/mainservice';
import axios from 'axios';
import { getDevicePlatform, getDeviceType } from 'helpers/deviceHelper';
import {
  deleteDelegateTokens,
  deleteTokens,
  getAccessToken,
  getDelegateAccessToken,
  getDeviceId,
  getOriginCompanyId,
  getRefreshToken,
  restoreDelegateTokens,
  saveAccessToken,
  saveDownloadUriTemplates,
  saveRefreshToken
} from 'helpers/tokenManager';

import authClient from '../authClient';

let requestQueue: Array<() => void> = []; // Request queue waiting to new token to be repeated
let refreshing = false;
let refreshPromise: Promise<LoginResult | undefined> | null = null;
let refreshNetworkCooldownError: unknown = null;
let refreshNetworkCooldownUntil = 0;
const TOKEN_REFRESH_LOCK_KEY = 'token-refresh-lock';
const REFRESH_NETWORK_COOLDOWN_MS = 5000;

const getNavigatorLocks = (): LockManager | undefined => {
  if (typeof navigator === 'undefined') {
    return undefined;
  }

  return (navigator as Navigator & { locks?: LockManager }).locks;
};

const repeatQueuedRequests = () => {
  const queued = requestQueue;
  requestQueue = [];
  queued.forEach((fn) => {
    try {
      fn();
    } catch {
      // swallow individual request errors here
    }
  });
};

const queueRequest = (request: () => void) => {
  requestQueue.push(request);
};

export const refreshToken = async (): Promise<LoginResult | undefined> => {
  if (refreshing && refreshPromise) {
    return refreshPromise;
  }

  if (refreshNetworkCooldownError && Date.now() < refreshNetworkCooldownUntil) {
    throw refreshNetworkCooldownError;
  }

  refreshNetworkCooldownError = null;
  refreshNetworkCooldownUntil = 0;

  refreshing = true;
  const refreshTokenAtStart = getRefreshToken();

  refreshPromise = (async () => {
    const runRefreshRequest = async () => {
      const currentAccessToken = getAccessToken();
      const currentRefreshToken = getRefreshToken();

      if (
        refreshTokenAtStart &&
        refreshTokenAtStart !== currentRefreshToken &&
        !!currentAccessToken &&
        !!currentRefreshToken
      ) {
        repeatQueuedRequests();
        return undefined;
      }

      if (!currentRefreshToken) {
        throw new Error('missing refresh token');
      }

      const response = await authClient.refreshTokens({
        refreshToken: currentRefreshToken,
        deviceInfo: {
          deviceIdentifier: getDeviceId(),
          deviceType: getDeviceType(),
          devicePlatform: getDevicePlatform()
        }
      });

      if (!response.data.user) {
        throw new Error(`error: ${response.data.error || ''}`);
      }

      saveAccessToken(response.data.accessToken!);
      saveRefreshToken(response.data.refreshToken!);
      saveDownloadUriTemplates(response.data.downloadUriTemplates!);
      refreshNetworkCooldownError = null;
      refreshNetworkCooldownUntil = 0;
      repeatQueuedRequests();

      return response.data;
    };

    try {
      const navigatorLocks = getNavigatorLocks();
      if (navigatorLocks) {
        return await navigatorLocks.request(TOKEN_REFRESH_LOCK_KEY, runRefreshRequest);
      }

      return await runRefreshRequest();
    } catch (error) {
      const isNetworkError =
        axios.isAxiosError(error) &&
        (!error.response ||
          error.code === 'ECONNABORTED' ||
          error.code === 'ERR_NETWORK' ||
          error.code === 'ERR_CANCELED');

      if (isNetworkError) {
        refreshNetworkCooldownError = error;
        refreshNetworkCooldownUntil = Date.now() + REFRESH_NETWORK_COOLDOWN_MS;
      }

      Sentry.captureException(error);
      requestQueue = [];

      // Only delete tokens for actual auth failures, not network errors

      if (!isNetworkError) {
        if (!getDelegateAccessToken()) {
          deleteTokens();
        } else {
          restoreDelegateTokens();
          const originCompanyId = getOriginCompanyId();
          originCompanyId
            ? window.location.replace(`/admin/companies/${originCompanyId}`)
            : window.location.replace('/');
          deleteDelegateTokens();
        }
      }

      throw error;
    } finally {
      refreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const repeatWhenPossible = (request: any) => {
  return new Promise((resolve, reject) => {
    queueRequest(() => {
      const token = getAccessToken();
      if (!token) {
        reject(new Error('token is missing after refresh'));
        return false;
      }
      request.headers = {
        ...(request.headers || {}),
        Authorization: `Bearer ${token}`
      };
      resolve(axios(request));
      return true;
    });
  });
};
